// Turns a parsed Snapchat warrant, Facebook or Instagram return into the
// input the shared analysis pages use (components/analysis/SocialAnalysis.js):
// one list of dated events, the account's changes, the people it's connected
// to, and any GPS points. Pure functions, so they can be tested on their own.

import { parseTime, isIp } from './analysisKit';

const T = (raw, zone = 'UTC') => {
  const p = parseTime(raw, zone);
  return p && !p.dateOnly ? p.t : p ? p.t : null;
};
const lc = s => String(s || '').trim().toLowerCase();
const short = (s, n = 140) => (String(s || '').length > n ? `${String(s).slice(0, n)}…` : String(s || ''));

function coords(lat, lon) {
  const a = parseFloat(lat), b = parseFloat(lon);
  if (!isFinite(a) || !isFinite(b) || (a === 0 && b === 0) || Math.abs(a) > 90 || Math.abs(b) > 180) return null;
  return { lat: a, lon: b };
}

// ─── Snapchat ────────────────────────────────────────────────────────────────

export function snapchatInput(data) {
  const { targetInfo, dateRange, subscriber, ipRecords, conversations, friends, friendsList, memories, stories } = data;
  const main = subscriber.main || {};
  const me = lc(main.username);
  const myId = targetInfo.userId || main.user_id || '';
  const account = main.username || myId || 'target';
  const events = [];
  const push = e => { if (e.t) events.push({ account, ips: [], places: [], ...e }); };

  for (const c of conversations) {
    const thread = c.isOneOnOne ? c.title : `Group: ${c.title}`;
    for (const m of c.messages) {
      const sent = (myId && m.sender_id === myId) || (me && lc(m.sender) === me);
      const other = sent
        ? (c.isOneOnOne ? (m.recipient || c.participants.find(p => lc(p) !== me) || c.title) : `Group: ${c.title}`)
        : m.sender;
      push({
        t: T(m.timestamp), kind: 'message', source: sent ? 'Chat sent' : 'Chat received', service: m.content_type || 'Chat',
        activity: sent, handle: other || '', direction: sent ? 'outgoing' : 'incoming', thread,
        ips: sent && isIp(m.upload_ip) ? [m.upload_ip] : [],
        detail: `${m.sender || '?'} → ${c.isOneOnOne ? (m.recipient || other) : thread}: ${short(m.text || `[${m.content_type || 'media'}]`)}`,
      });
    }
  }
  for (const r of ipRecords) {
    push({ t: T(r.timestamp), kind: 'ip', source: `IP log${r.source ? ` (${r.source})` : ''}`, activity: true,
      ips: isIp(r.ip) ? [r.ip] : [], detail: [r.user_agent, r.device_id && `device ${r.device_id}`].filter(Boolean).join(' · ') });
  }
  const places = [];
  for (const m of memories) {
    const t = T(m.timestamp), at = coords(m.latitude, m.longitude);
    push({ t, kind: 'memory', source: 'Memory saved', activity: true, places: at ? [`${at.lat}, ${at.lon}`] : [], detail: [m.source_type, m.duration && `${m.duration}s`, m.media_id].filter(Boolean).join(' · ') });
    if (t && at) places.push({ t, ...at, source: 'Memory', detail: [m.source_type, m.media_id].filter(Boolean).join(' · ') });
  }
  for (const s of stories) {
    push({ t: T(s.timestamp), kind: 'story', source: 'Story posted', activity: true, ips: isIp(s.upload_ip) ? [s.upload_ip] : [], detail: [s.media_type, s.media_id].filter(Boolean).join(' · ') });
  }

  // Friends: who added whom, blocks, and anyone no longer on the current list.
  const current = new Set(friendsList.map(lc));
  const friendRows = friends.map(f => {
    const byMe = lc(f.adder) === me;
    const who = byMe ? f.added : f.adder;
    const blocked = /block/i.test(f.type);
    const what = blocked ? (byMe ? 'Account blocked them' : 'They blocked the account')
      : /friend/i.test(f.type) ? (byMe ? 'Account added them' : 'They added the account') : `${f.type} (${byMe ? 'by the account' : 'by them'})`;
    const flag = !blocked && current.size && !current.has(lc(who)) ? 'not on the current friends list' : '';
    return { t: T(f.created), who, what, detail: (f.friending_method || '').replace(/_/g, ' ').toLowerCase(), flag };
  });
  for (const r of friendRows) push({ t: r.t, kind: 'friend', source: r.what, activity: /^Account/.test(r.what), detail: `${r.what}: ${r.who}` });
  const connectedSet = new Set([...current, ...friends.filter(f => /friend/i.test(f.type)).map(f => lc(lc(f.adder) === me ? f.added : f.adder))]);

  const changes = [];
  if (main.created) changes.push({ t: T(main.created), type: 'Account created', detail: main.username, ip: isIp(main.creation_ip) ? main.creation_ip : '' });
  for (const u of subscriber.usernameHistory || []) changes.push({ t: T(u.timestamp), type: 'Username changed', detail: `${u.old} → ${u.new}` });
  for (const c of subscriber.accountChanges || []) {
    changes.push({ t: T(c.date), type: c.action || 'Account change', detail: [[c.old_value, c.new_value].filter(Boolean).join(' → '), c.reason].filter(Boolean).join(' · ') });
  }
  const ext = subscriber.extended || {};
  if (ext.email_verified) changes.push({ t: T(ext.email_verified), type: 'Email verified', detail: main.email });
  if (ext.phone_verified) changes.push({ t: T(ext.phone_verified), type: 'Phone verified', detail: main.phone });

  return {
    source: 'Snap', title: 'Snapchat Warrant Return', scope: `@${account}`,
    events: events.sort((a, b) => a.t - b.t),
    ownKeys: new Set([me, lc(main.email), String(main.phone || '').replace(/\D/g, '').replace(/^1(?=\d{10}$)/, '')].filter(Boolean)),
    connected: { label: 'friends', size: connectedSet.size, match: h => connectedSet.has(lc(h)) },
    people: {
      title: 'Friends timeline', sub: 'Friend adds and blocks in order, and friends no longer on the current friends list.',
      explain: 'Each row is one friend or block record from the return, in time order. A friend who isn\'t on the friends list the return was produced with (marked) was removed, removed the account, or deleted their own account. A burst of new friends or blocks around the incident is worth a look.',
      rows: friendRows,
    },
    changes: changes.filter(c => c.t),
    places,
    profile: [
      ['Username', main.username], ['User ID', myId], ['Display name', main.display_name], ['Email', main.email], ['Phone', main.phone],
      ['Created', main.created], ['Creation IP', main.creation_ip], ['Status', main.status], ['Date range searched', dateRange],
    ],
  };
}

// ─── Facebook and Instagram (Meta) ───────────────────────────────────────────

// "Jane Doe (100004567890)" / "jane.doe (Instagram: 1784…)" → name, id and a display label.
export function metaPerson(s) {
  const raw = String(s || '').trim();
  const id = (raw.match(/\((?:[A-Za-z ]+:\s*)?(\d{5,})\)/) || [])[1] || '';
  const name = raw.replace(/\s*\([^)]*\)\s*$/, '').trim() || raw;
  return { id, name, label: id ? `${name} (${id})` : name };
}

function personMatcher(list) {
  const ids = new Set(), names = new Set();
  for (const s of list) {
    const p = metaPerson(s);
    if (p.id) ids.add(p.id);
    if (p.name) names.add(lc(p.name));
  }
  return { size: Math.max(ids.size, names.size), match: h => { const p = metaPerson(h); return (p.id && ids.has(p.id)) || names.has(lc(p.name)); } };
}

// kv_pairs grouped into records: [{ t, values: [[label, value]] }]
function kvRecords(section) {
  const by = new Map();
  for (const kv of section?.kv_pairs || []) {
    if (!by.has(kv.record)) by.set(kv.record, []);
    by.get(kv.record).push([kv.label, kv.value]);
  }
  return [...by.values()].map(values => {
    const timed = values.find(([, v]) => parseTime(v, 'UTC'));
    return { t: timed ? T(timed[1]) : null, values, timeLabel: timed?.[0] };
  });
}

function messageEvents(threadsBySection, meNames, myId, push) {
  const isMe = s => { const p = metaPerson(s); return (myId && p.id === myId) || meNames.has(lc(p.name)); };
  for (const [section, threads] of Object.entries(threadsBySection || {})) {
    const label = section.replace(/_/g, ' ');
    for (const th of threads) {
      const others = th.participants.filter(p => !isMe(p));
      const oneToOne = others.length === 1;
      const thread = th.thread_name || (others.length ? others.map(p => metaPerson(p).name).join(', ') : `Thread ${th.thread_id}`);
      for (const m of th.messages) {
        const sent = isMe(m.author);
        const other = sent ? (oneToOne ? metaPerson(others[0]).label : `Group: ${thread}`) : metaPerson(m.author).label;
        push({
          t: T(m.sent), kind: 'message', source: sent ? 'Message sent' : 'Message received', service: label,
          activity: sent, handle: other, direction: sent ? 'outgoing' : 'incoming', thread: `${thread} (${th.thread_id})`,
          detail: `${metaPerson(m.author).name}: ${short(m.body || (m.removed ? '[removed by sender]' : '[no text]'))}`,
        });
      }
    }
  }
}

function ipEvents(records, label, push) {
  for (const r of records || []) {
    push({ t: T(r.time), kind: 'ip', source: label, activity: true, ips: isIp(r.ip) ? [r.ip] : [], detail: [r.action, r.agent].filter(Boolean).join(' · ') });
  }
}

function placesFrom(records, label) {
  const out = [];
  for (const r of records) {
    const text = r.values.map(([, v]) => v).join(' ');
    const lat = r.values.find(([k]) => /latitude/i.test(k))?.[1], lon = r.values.find(([k]) => /longitude/i.test(k))?.[1];
    const pair = text.match(/(-?\d{1,2}\.\d{3,})\s*,\s*(-?\d{1,3}\.\d{3,})/);
    const at = coords(lat ?? pair?.[1], lon ?? pair?.[2]);
    if (at && r.t) out.push({ t: r.t, ...at, source: label, detail: short(r.values.filter(([k]) => !/latitude|longitude|time|date|created/i.test(k)).map(([k, v]) => `${k}: ${v}`).join(' · '), 160) });
  }
  return out;
}

function changesFrom(sections, keys) {
  const out = [];
  for (const key of keys) {
    const sec = sections[key];
    if (!sec || !sec.count) continue;
    for (const r of kvRecords(sec)) {
      const ip = r.values.map(([, v]) => v).find(isIp) || '';
      out.push({ t: r.t, type: sec.label, ip, detail: short(r.values.filter(([k, v]) => k !== r.timeLabel && v !== ip).map(([k, v]) => `${k}: ${v}`).join(' · '), 200) });
    }
  }
  return out;
}

export function facebookInput(data) {
  const { subject_id: myId, summary, sections, message_threads: threads, ip_records: ips, photos } = data;
  const me = lc(summary.name);
  const account = summary.name || myId || 'target';
  const events = [];
  const push = e => { if (e.t) events.push({ account, ips: [], places: [], ...e }); };
  messageEvents(threads, new Set([me].filter(Boolean)), myId, push);
  ipEvents(ips.ip_addresses, 'IP addresses', push);
  ipEvents(ips.active_sessions, 'Active sessions', push);
  ipEvents(ips.security_logs, 'Security logs', push);
  for (const p of photos || []) push({ t: T(p.taken), kind: 'photo', source: 'Photo uploaded', activity: true, ips: isIp(p.uploadIp) ? [p.uploadIp] : [], detail: [p.id, p.privacy].filter(Boolean).join(' · ') });
  for (const key of ['status_updates', 'wallposts', 'shares', 'posts_to_other_walls']) {
    for (const r of kvRecords(sections[key])) push({ t: r.t, kind: 'post', source: sections[key].label, activity: true, detail: short(r.values.filter(([k]) => k !== r.timeLabel).map(([k, v]) => `${k}: ${v}`).join(' · ')) });
  }

  const places = [
    ...placesFrom(kvRecords(sections.checkins), 'Check-in'),
    ...placesFrom(kvRecords(sections.last_location), 'Last location'),
    ...placesFrom(kvRecords(sections.location_services), 'Location services'),
  ];
  for (const p of places) push({ t: p.t, kind: 'place', source: p.source, activity: true, places: [`${p.lat}, ${p.lon}`], detail: p.detail });

  const changes = changesFrom(sections, ['name_changes', 'email_changes', 'vanity_changes', 'password_changes', 'account_status_history', 'removed_phone_numbers']);
  for (const r of ips.security_logs || []) changes.push({ t: T(r.time), type: `Security log: ${r.action || 'event'}`, detail: r.agent, ip: isIp(r.ip) ? r.ip : '' });
  if (summary.registration_date) changes.push({ t: T(summary.registration_date), type: 'Account registered', detail: '', ip: isIp(summary.registration_ip) ? summary.registration_ip : '' });

  const personOf = r => r.values.find(([k]) => /^name$/i.test(k))?.[1] || r.values.find(([k, v]) => k !== r.timeLabel && v)?.[1] || '';
  const friendList = kvRecords(sections.friends).map(personOf).filter(Boolean);
  const people = [
    ...kvRecords(sections.friends).map(r => ({ t: r.t, who: metaPerson(personOf(r)).label, what: 'Friend', detail: '' })),
    ...kvRecords(sections.removed_friends).map(r => ({ t: r.t, who: metaPerson(personOf(r)).label, what: 'Removed friend', detail: '', flag: 'removed' })),
    ...kvRecords(sections.friend_requests).map(r => ({ t: r.t, who: metaPerson(personOf(r)).label, what: 'Friend request', detail: short(r.values.filter(([k]) => !/^name$/i.test(k) && k !== r.timeLabel).map(([k, v]) => `${k}: ${v}`).join(' · ')) })),
  ].filter(r => r.who);

  return {
    source: 'Meta (Facebook)', title: 'Facebook Return', scope: `${summary.name || 'Account'}${myId ? ` (${myId})` : ''}`,
    events: events.sort((a, b) => a.t - b.t),
    ownKeys: new Set([lc(metaPerson(account).label), me].filter(Boolean)),
    connected: friendList.length ? { label: 'friends', ...personMatcher(friendList) } : null,
    people: people.length ? {
      title: 'Friends timeline', sub: 'Friends, removed friends and friend requests, with their dates where the return has them.',
      explain: 'Each row is one friend, removed-friend or friend-request record from the return, in time order. Removed friends and new friends around the incident are worth a look.',
      rows: people,
    } : null,
    changes: changes.filter(c => c.t),
    places,
    profile: [
      ['Name', summary.name], ['Account ID', myId], ['Emails', summary.emails], ['Phones', summary.phone_numbers],
      ['Registered', summary.registration_date], ['Registration IP', summary.registration_ip], ['City', summary.current_city], ['Account ended', summary.account_end_date],
    ],
  };
}

export function instagramInput(data) {
  const { subject_id: myId, summary, sections, message_threads: threads, ip_records: ips, followers, following, followReqs, photos, videos, comments, likes } = data;
  const vanity = lc(summary.vanity);
  const account = summary.vanity || summary.name || myId || 'target';
  const events = [];
  const push = e => { if (e.t) events.push({ account, ips: [], places: [], ...e }); };
  // Messages name the account by its username or, in some returns, its display name.
  messageEvents(threads, new Set([vanity, lc(summary.name)].filter(Boolean)), myId, push);
  ipEvents(ips, 'IP addresses', push);
  for (const p of [...(photos || []), ...(videos || [])]) push({ t: T(p.taken), kind: 'media', source: 'Photo or video posted', activity: true, ips: isIp(p.uploadIp) ? [p.uploadIp] : [], detail: [p.id, p.privacy].filter(Boolean).join(' · ') });
  for (const c of comments || []) push({ t: T(c.dateCreated), kind: 'comment', source: 'Comment', activity: true, detail: short(`${c.owner ? `on ${c.owner}'s post: ` : ''}${c.text}`) });
  for (const l of likes || []) push({ t: T(l.likeTimestamp), kind: 'like', source: 'Like', activity: true, detail: l.author ? `liked a post by ${l.author}` : 'liked a post' });

  const followerNames = new Set((followers || []).map(f => lc(metaPerson(f.raw).name)));
  const followingNames = new Set((following || []).map(f => lc(metaPerson(f.raw).name)));
  const people = [
    ...(followers || []).map(f => ({ t: T(f.timestamp), who: metaPerson(f.raw).label, what: 'Follows the account', detail: '', flag: followingNames.has(lc(metaPerson(f.raw).name)) ? '' : 'not followed back' })),
    ...(following || []).map(f => ({ t: T(f.timestamp), who: metaPerson(f.raw).label, what: 'Account follows them', detail: '', flag: followerNames.has(lc(metaPerson(f.raw).name)) ? '' : "doesn't follow back" })),
    ...(followReqs || []).map(f => ({ t: T(f.timestamp), who: metaPerson(f.raw).label, what: 'Follow request to the account', detail: '' })),
  ];
  const changes = changesFrom(sections, ['vanity_changes']);
  if (summary.registration_date) changes.push({ t: T(summary.registration_date), type: 'Account registered', detail: '', ip: isIp(summary.registration_ip) ? summary.registration_ip : '' });

  const connected = [...(followers || []), ...(following || [])].map(f => f.raw);
  return {
    source: 'Meta (Instagram)', title: 'Instagram Return', scope: `@${account}${myId ? ` (${myId})` : ''}`,
    events: events.sort((a, b) => a.t - b.t),
    ownKeys: new Set([vanity, lc(summary.name), lc(metaPerson(account).label)].filter(Boolean)),
    connected: connected.length ? { label: 'followers or following', ...personMatcher(connected) } : null,
    people: people.length ? {
      title: 'Followers timeline', sub: 'Followers, accounts followed and follow requests, in order, and who doesn\'t follow back.',
      explain: 'Each row is one follower, following or follow-request record, in time order where the return has a time. One-way follows are marked: an account that follows the target without being followed back (or the reverse) is a different kind of relationship from a mutual one.',
      rows: people,
    } : null,
    changes: changes.filter(c => c.t),
    places: [],
    profile: [
      ['Username', summary.vanity], ['Name', summary.name], ['Account ID', myId], ['Emails', summary.emails], ['Phones', summary.phone_numbers],
      ['Registered', summary.registration_date], ['Registration IP', summary.registration_ip], ['Still active', summary.account_active],
    ],
  };
}
